import { http } from '@/lib/http';

export type JobStatus = 'draft' | 'started' | 'completed' | 'cancelled';
export type PaymentStatus = 'unpaid' | 'paid';
export type RecurrenceFrequency = 'weekly' | 'monthly' | 'custom';

/**
 * Payment-method enum shared by a job's `default_payment_method` and a visit's
 * `payment_method`. The API rejects anything outside this set with a 400;
 * "no default" is `null` (or the field omitted).
 */
export type PaymentMethod =
  | 'cash'
  | 'check'
  | 'zelle'
  | 'venmo'
  | 'stripe'
  | 'other';

/** Scope for the bulk job-crew endpoints — mirrors `scope` in `controller.bulkJobCrewRequest`. */
export type JobCrewScope = 'all' | 'upcoming' | 'unstarted';

/**
 * Request-side recurrence rule — mirrors `controller.recurrenceRequest`.
 * On write the rule is a nested object; on read its fields are flattened
 * onto `JobDto` (see `frequency` etc. below).
 */
export interface JobRecurrence {
  frequency: RecurrenceFrequency;
  days_of_week?: number[];
  day_of_month?: number;
  interval_count?: number;
  series_end_date?: string;
  custom_rule?: string;
}

/**
 * Mirrors `repo.Job` in the vizitpro-api swagger spec. A job is one row for
 * the logical piece of work; the recurrence rule is flattened onto it
 * (`frequency == null` ⇒ a one-time job). All execution / payment / crew
 * state lives on its `job_visit` rows, not here.
 */
export interface JobDto {
  id: string;
  business_id: string;
  customer_user_id: string;
  customer_name: string;
  title: string;
  description: string | null;
  start_date: string;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
  service_address: string | null;
  timezone: string | null;
  /** Default amount per visit; each visit can override it. */
  total_amount: number | null;
  /**
   * Copied into each `job_visit.payment_method` when visits are seeded at
   * job-creation time. `null` ⇒ no default.
   */
  default_payment_method: PaymentMethod | null;
  /**
   * Optionally points at a specific `payment_methods` row (e.g. one of
   * several configured Venmo accounts), copied onto each seeded visit's
   * `payment_method_id`. `null` ⇒ no specific account, just the type above.
   */
  default_payment_method_id: string | null;
  // ─── Flattened recurrence rule (all null on a one-time job) ───────────────
  frequency: RecurrenceFrequency | null;
  interval_count: number | null;
  days_of_week: number[] | null;
  day_of_month: number | null;
  custom_rule: string | null;
  series_end_date: string | null;
  /** Number of visits materialised for this job. */
  visit_count: number;
  /**
   * Distinct employees assigned across all of this job's visits, denormalized
   * onto the list + detail responses. `undefined` means the API hasn't
   * populated it — callers should treat that as "unknown" and render no badge.
   */
  assigned_employee_count?: number | null;
  created_at: string;
  updated_at: string;
}

/**
 * Request body for `POST /businesses/{business_id}/jobs` — mirrors
 * `controller.createJobRequest`. `customer_user_id` must reference an
 * active customer of the business; `start_date` and `title` are also
 * required. Backs the Add Job modal (`src/routes/jobs/add-job-modal.tsx`).
 */
export interface CreateJobRequest {
  customer_user_id: string;
  start_date: string;
  title: string;
  description?: string;
  end_date?: string;
  end_time?: string;
  service_address?: string;
  start_time?: string;
  timezone?: string;
  total_amount?: number;
  /** One of the `PaymentMethod` values, or `null` for "no default". */
  default_payment_method?: PaymentMethod | null;
  /** A specific `payment_methods.id` (e.g. one of several Venmo accounts). */
  default_payment_method_id?: string | null;
  recurrence?: JobRecurrence;
}

/**
 * Request body for `PATCH /businesses/{business_id}/jobs/{job_id}` — mirrors
 * `controller.updateJobRequest`. Edits the logical job, including its
 * recurrence rule (`recurrence` / `clear_recurrence`); it does not
 * regenerate existing visits. Execution and payment state live on visits.
 */
export interface UpdateJobRequest {
  title?: string;
  description?: string;
  start_date?: string;
  start_time?: string;
  end_date?: string;
  end_time?: string;
  service_address?: string;
  timezone?: string;
  total_amount?: number;
  /**
   * One of the `PaymentMethod` values, or `null` to clear the default.
   * Changing only this does not touch existing visits.
   */
  default_payment_method?: PaymentMethod | null;
  /** A specific `payment_methods.id`, or `null` to clear it. */
  default_payment_method_id?: string | null;
  recurrence?: JobRecurrence;
  clear_recurrence?: boolean;
}

export interface ListJobsFilters {
  customer_user_id?: string;
  start_date_from?: string;
  start_date_to?: string;
}

export function jobsQueryKey(businessId: string, filters: ListJobsFilters) {
  return ['jobs', businessId, filters] as const;
}

export function jobQueryKey(businessId: string, jobId: string) {
  return ['jobs', businessId, jobId] as const;
}

/**
 * Calls `GET /businesses/{business_id}/jobs` (one row per logical job, not
 * per visit), optionally filtered by customer and/or a start-date range.
 */
export async function listJobs(
  businessId: string,
  filters: ListJobsFilters = {},
): Promise<JobDto[]> {
  const { data } = await http.get<JobDto[]>(`/businesses/${businessId}/jobs`, {
    params: filters,
  });
  return data;
}

/** Calls `GET /businesses/{business_id}/jobs/{job_id}`. */
export async function getJob(businessId: string, jobId: string): Promise<JobDto> {
  const { data } = await http.get<JobDto>(`/businesses/${businessId}/jobs/${jobId}`);
  return data;
}

/**
 * Calls `POST /businesses/{business_id}/jobs`. Also materialises visits (1 for
 * a one-time job, N for a recurring one). Caller must be an owner or an
 * employee granted `create_jobs`.
 */
export async function createJob(
  businessId: string,
  body: CreateJobRequest,
): Promise<JobDto> {
  const { data } = await http.post<JobDto>(`/businesses/${businessId}/jobs`, body);
  return data;
}

/** Calls `PATCH /businesses/{business_id}/jobs/{job_id}`. Caller must be an owner. */
export async function updateJob(
  businessId: string,
  jobId: string,
  body: UpdateJobRequest,
): Promise<JobDto> {
  const { data } = await http.patch<JobDto>(
    `/businesses/${businessId}/jobs/${jobId}`,
    body,
  );
  return data;
}

// ─── Job visits ─────────────────────────────────────────────────────────────
// A visit is one concrete occurrence of a job. All execution + payment state
// lives here. Visit endpoints are owner-only.

/** Mirrors `repo.JobVisit` in the vizitpro-api swagger spec. */
export interface JobVisitDto {
  id: string;
  job_id: string;
  business_id: string;
  /** 1-based position within the job's visit list. */
  seq: number;
  scheduled_date: string;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
  status: JobStatus;
  payment_status: PaymentStatus;
  total_amount: number | null;
  completed_at: string | null;
  paid_at: string | null;
  amount_collected: number | null;
  /** Seeded from the job's `default_payment_method`; constrained to the enum. */
  payment_method: PaymentMethod | null;
  /** Seeded from the job's `default_payment_method_id`; a specific configured account, or `null`. */
  payment_method_id: string | null;
  created_at: string;
  updated_at: string;
}

/** Mirrors `repo.JobVisitEmployee` — one crew assignment on a visit. */
export interface JobVisitEmployeeDto {
  id: string;
  job_visit_id: string;
  business_id: string;
  employee_id: string;
  employee_full_name: string;
  employee_email: string | null;
  pay_amount: number | null;
  created_at: string;
  updated_at: string;
}

/** Mirrors `repo.JobVisitWithCrew` — a visit plus its crew (never nil; [] when none). */
export interface JobVisitWithCrewDto extends JobVisitDto {
  assigned_employees: JobVisitEmployeeDto[];
}

// ─── Business-wide visits ───────────────────────────────────────────────────
// A read model for the jobs-page calendar: every visit across every job in
// the business, in a date range — unlike `listJobVisits`, which is scoped to
// one job and has no date filter.

/** Mirrors `repo.BusinessVisit` — a visit plus enough of its parent job to plot and filter it without a second request. */
export interface BusinessVisitDto {
  id: string;
  job_id: string;
  job_title: string;
  /** The parent job's recurrence frequency; `null` for a one-time job. */
  job_frequency: RecurrenceFrequency | null;
  /** The parent job's denormalized crew count — same field `JobDto` sends. */
  job_assigned_employee_count: number | null;
  scheduled_date: string;
  status: JobStatus;
}

export interface ListBusinessVisitsFilters {
  scheduled_date_from: string;
  scheduled_date_to: string;
}

export function businessVisitsQueryKey(
  businessId: string,
  filters: ListBusinessVisitsFilters,
) {
  return ['business-visits', businessId, filters] as const;
}

/**
 * Calls `GET /businesses/{business_id}/visits` — every visit in the business
 * whose `scheduled_date` falls in `[scheduled_date_from, scheduled_date_to]`
 * (both required), across all jobs. Owner-only, same scope as `listJobVisits`.
 */
export async function listBusinessVisits(
  businessId: string,
  filters: ListBusinessVisitsFilters,
): Promise<BusinessVisitDto[]> {
  const { data } = await http.get<BusinessVisitDto[]>(
    `/businesses/${businessId}/visits`,
    { params: filters },
  );
  return data;
}

/**
 * Body for `PATCH .../jobs/{job_id}/visits/{visit_id}` — mirrors
 * `controller.updateJobVisitRequest`. Status transitions are constrained
 * server-side (draft → started → completed/cancelled) and `payment_status`
 * cannot revert from `paid`; callers should surface the server's 400.
 */
export interface UpdateJobVisitRequest {
  scheduled_date?: string;
  start_time?: string;
  end_date?: string;
  end_time?: string;
  status?: JobStatus;
  payment_status?: PaymentStatus;
  total_amount?: number;
  amount_collected?: number;
  /** Must be one of the `PaymentMethod` values — legacy free text now 400s. */
  payment_method?: PaymentMethod;
  /** A specific `payment_methods.id`, or `null` to clear it. */
  payment_method_id?: string | null;
}

/**
 * Body for `POST .../visits/{visit_id}/employees` — mirrors
 * `controller.assignJobVisitEmployeeRequest`. `employee_id` is required.
 */
export interface AssignJobVisitEmployeeRequest {
  employee_id: string;
  pay_amount?: number;
}

/**
 * Body for `PATCH .../visits/{visit_id}/employees/{id}` — mirrors
 * `controller.updateJobVisitEmployeeRequest`. Only the pay amount is editable.
 */
export interface UpdateJobVisitEmployeeRequest {
  pay_amount: number;
}

export function jobVisitsQueryKey(businessId: string, jobId: string) {
  return ['jobs', businessId, jobId, 'visits'] as const;
}

export function jobVisitQueryKey(
  businessId: string,
  jobId: string,
  visitId: string,
) {
  return ['jobs', businessId, jobId, 'visits', visitId] as const;
}

export function jobVisitEmployeesQueryKey(
  businessId: string,
  jobId: string,
  visitId: string,
) {
  return ['jobs', businessId, jobId, 'visits', visitId, 'employees'] as const;
}

/**
 * Calls `GET /businesses/{business_id}/jobs/{job_id}/visits` — every visit of
 * a job with its crew, ordered by sequence. Owner-only.
 */
export async function listJobVisits(
  businessId: string,
  jobId: string,
): Promise<JobVisitWithCrewDto[]> {
  const { data } = await http.get<JobVisitWithCrewDto[]>(
    `/businesses/${businessId}/jobs/${jobId}/visits`,
  );
  return data;
}

/** Calls `GET /businesses/{business_id}/jobs/{job_id}/visits/{visit_id}`. Owner-only. */
export async function getJobVisit(
  businessId: string,
  jobId: string,
  visitId: string,
): Promise<JobVisitDto> {
  const { data } = await http.get<JobVisitDto>(
    `/businesses/${businessId}/jobs/${jobId}/visits/${visitId}`,
  );
  return data;
}

/** Calls `PATCH /businesses/{business_id}/jobs/{job_id}/visits/{visit_id}`. Owner-only. */
export async function updateJobVisit(
  businessId: string,
  jobId: string,
  visitId: string,
  body: UpdateJobVisitRequest,
): Promise<JobVisitDto> {
  const { data } = await http.patch<JobVisitDto>(
    `/businesses/${businessId}/jobs/${jobId}/visits/${visitId}`,
    body,
  );
  return data;
}

/** Calls `GET /businesses/{business_id}/jobs/{job_id}/visits/{visit_id}/employees`. */
export async function listJobVisitEmployees(
  businessId: string,
  jobId: string,
  visitId: string,
): Promise<JobVisitEmployeeDto[]> {
  const { data } = await http.get<JobVisitEmployeeDto[]>(
    `/businesses/${businessId}/jobs/${jobId}/visits/${visitId}/employees`,
  );
  return data;
}

/** Calls `POST /businesses/{business_id}/jobs/{job_id}/visits/{visit_id}/employees`. */
export async function assignJobVisitEmployee(
  businessId: string,
  jobId: string,
  visitId: string,
  body: AssignJobVisitEmployeeRequest,
): Promise<JobVisitEmployeeDto> {
  const { data } = await http.post<JobVisitEmployeeDto>(
    `/businesses/${businessId}/jobs/${jobId}/visits/${visitId}/employees`,
    body,
  );
  return data;
}

/** Calls `PATCH /businesses/{business_id}/jobs/{job_id}/visits/{visit_id}/employees/{id}`. */
export async function updateJobVisitEmployee(
  businessId: string,
  jobId: string,
  visitId: string,
  assignmentId: string,
  body: UpdateJobVisitEmployeeRequest,
): Promise<JobVisitEmployeeDto> {
  const { data } = await http.patch<JobVisitEmployeeDto>(
    `/businesses/${businessId}/jobs/${jobId}/visits/${visitId}/employees/${assignmentId}`,
    body,
  );
  return data;
}

/** Calls `DELETE /businesses/{business_id}/jobs/{job_id}/visits/{visit_id}/employees/{id}`. */
export async function unassignJobVisitEmployee(
  businessId: string,
  jobId: string,
  visitId: string,
  assignmentId: string,
): Promise<void> {
  await http.delete(
    `/businesses/${businessId}/jobs/${jobId}/visits/${visitId}/employees/${assignmentId}`,
  );
}

// ─── Job summary ────────────────────────────────────────────────────────────
// Aggregate view of one job across all its visits. Owner-only.

/**
 * Read form of the recurrence rule, nested inside `JobSummaryDto`
 * (`repo.JobRecurrence`). `null` on the summary of a one-time job.
 */
export interface JobRecurrenceRuleDto {
  frequency: RecurrenceFrequency;
  interval_count: number | null;
  days_of_week: number[] | null;
  day_of_month: number | null;
  custom_rule: string | null;
  series_end_date: string | null;
}

/** Mirrors `repo.SeriesVisitCounts`. */
export interface SeriesVisitCounts {
  draft: number;
  started: number;
  completed: number;
  cancelled: number;
  total: number;
}

/** Mirrors `repo.SeriesPaymentRollup`. */
export interface SeriesPaymentRollup {
  completed_total: number;
  collected_total: number;
  paid_total: number;
  paid_count: number;
  unpaid_count: number;
}

/** Mirrors `repo.SeriesEmployeePay`. */
export interface SeriesEmployeePay {
  employee_id: string;
  full_name: string;
  visits_assigned: number;
  total_pay: number;
}

/** Mirrors `repo.SeriesLaborRollup`. */
export interface SeriesLaborRollup {
  total_pay: number;
  by_employee: SeriesEmployeePay[];
}

/** Mirrors `repo.JobSummary` — the aggregate view of one job. */
export interface JobSummaryDto {
  /** `null` for a one-time job. */
  recurrence: JobRecurrenceRuleDto | null;
  visits: SeriesVisitCounts;
  payment: SeriesPaymentRollup;
  labor: SeriesLaborRollup;
  first_visit_date: string | null;
  next_visit_date: string | null;
  last_visit_date: string | null;
  last_completed_at: string | null;
}

/** Scope for the bulk job-crew endpoints — `all` is the server-side default. */
export interface BulkJobCrewRequest {
  employee_id: string;
  pay_amount?: number;
  scope: JobCrewScope;
}

/** Mirrors `service.BulkJobCrewResult`. */
export interface BulkJobCrewResult {
  visits_matched: number;
  rows_affected: number;
}

export function jobSummaryQueryKey(businessId: string, jobId: string) {
  return ['jobs', businessId, jobId, 'summary'] as const;
}

/** Calls `GET /businesses/{business_id}/jobs/{job_id}/summary`. Owner-only. */
export async function getJobSummary(
  businessId: string,
  jobId: string,
): Promise<JobSummaryDto> {
  const { data } = await http.get<JobSummaryDto>(
    `/businesses/${businessId}/jobs/${jobId}/summary`,
  );
  return data;
}

/**
 * Calls `POST /businesses/{business_id}/jobs/{job_id}/crew` — assigns an
 * employee across every visit of the job in scope, skipping visits they are
 * already on.
 */
export async function bulkAssignJobCrew(
  businessId: string,
  jobId: string,
  body: BulkJobCrewRequest,
): Promise<BulkJobCrewResult> {
  const { data } = await http.post<BulkJobCrewResult>(
    `/businesses/${businessId}/jobs/${jobId}/crew`,
    body,
  );
  return data;
}

/**
 * Calls `PATCH /businesses/{business_id}/jobs/{job_id}/crew` — sets an
 * employee's pay on every in-scope visit. `pay_amount` is required.
 */
export async function bulkSetJobCrewPay(
  businessId: string,
  jobId: string,
  body: BulkJobCrewRequest,
): Promise<BulkJobCrewResult> {
  const { data } = await http.patch<BulkJobCrewResult>(
    `/businesses/${businessId}/jobs/${jobId}/crew`,
    body,
  );
  return data;
}

/**
 * Calls `DELETE /businesses/{business_id}/jobs/{job_id}/crew` — unassigns an
 * employee across every in-scope visit. Takes a JSON body, so the payload
 * rides in `config.data`.
 */
export async function bulkUnassignJobCrew(
  businessId: string,
  jobId: string,
  body: BulkJobCrewRequest,
): Promise<BulkJobCrewResult> {
  const { data } = await http.delete<BulkJobCrewResult>(
    `/businesses/${businessId}/jobs/${jobId}/crew`,
    { data: body },
  );
  return data;
}

// ─── Job notes ──────────────────────────────────────────────────────────────

/** Mirrors `repo.JobNote` in the swagger spec. */
export interface JobNoteDto {
  id: string;
  job_id: string;
  author_user_id: string;
  author_full_name: string;
  body: string;
  created_at: string;
}

/** Body for `POST .../jobs/{job_id}/notes` — mirrors `controller.createJobNoteRequest`. */
export interface CreateJobNoteRequest {
  body: string;
}

export function jobNotesQueryKey(businessId: string, jobId: string) {
  return ['jobs', businessId, jobId, 'notes'] as const;
}

/** Calls `GET /businesses/{business_id}/jobs/{job_id}/notes`. Oldest first. */
export async function listJobNotes(
  businessId: string,
  jobId: string,
): Promise<JobNoteDto[]> {
  const { data } = await http.get<JobNoteDto[]>(
    `/businesses/${businessId}/jobs/${jobId}/notes`,
  );
  return data;
}

/** Calls `POST /businesses/{business_id}/jobs/{job_id}/notes`. */
export async function createJobNote(
  businessId: string,
  jobId: string,
  body: CreateJobNoteRequest,
): Promise<JobNoteDto> {
  const { data } = await http.post<JobNoteDto>(
    `/businesses/${businessId}/jobs/${jobId}/notes`,
    body,
  );
  return data;
}

// ─── Job attachments ────────────────────────────────────────────────────────

export type JobAttachmentFileType = 'image' | 'document';

/** Mirrors `repo.JobAttachment` in the swagger spec. */
export interface JobAttachmentDto {
  id: string;
  job_id: string;
  file_type: JobAttachmentFileType;
  file_url: string;
  caption: string | null;
  uploaded_by_user_id: string;
  uploaded_by_full_name: string;
  created_at: string;
}

/**
 * Body for `POST .../jobs/{job_id}/attachments` — mirrors
 * `controller.createJobAttachmentRequest`. The client uploads the file
 * elsewhere (Firebase Storage) and passes the resulting `file_url`.
 */
export interface CreateJobAttachmentRequest {
  file_type: JobAttachmentFileType;
  file_url: string;
  caption?: string;
}

export function jobAttachmentsQueryKey(businessId: string, jobId: string) {
  return ['jobs', businessId, jobId, 'attachments'] as const;
}

/** Calls `GET /businesses/{business_id}/jobs/{job_id}/attachments`. Oldest first. */
export async function listJobAttachments(
  businessId: string,
  jobId: string,
): Promise<JobAttachmentDto[]> {
  const { data } = await http.get<JobAttachmentDto[]>(
    `/businesses/${businessId}/jobs/${jobId}/attachments`,
  );
  return data;
}

/** Calls `POST /businesses/{business_id}/jobs/{job_id}/attachments`. */
export async function createJobAttachment(
  businessId: string,
  jobId: string,
  body: CreateJobAttachmentRequest,
): Promise<JobAttachmentDto> {
  const { data } = await http.post<JobAttachmentDto>(
    `/businesses/${businessId}/jobs/${jobId}/attachments`,
    body,
  );
  return data;
}
